import { render, type JSXChild } from 'engine/index';
import { rpc } from 'src/backend';
import { Table, Td, Th, TrBody, TrHead } from './TableComponents';
import { Button } from './Button';
import type { ReflectoraPlayerState, ReflectoraState } from 'shared/modelTypes/reflectora';
import { CheckboxField, ToggleField } from './FormComponents';

export function PlayReflectora({ licenseCode }: { licenseCode: string }) {
	const view: HTMLElement = <div>Will play game {licenseCode}</div>;

	const sessionId = licenseCode;

	rpc.onAdminUpdates.subscribe(
		{ sessionId },
		{
			onData(data) {
				//
				if (data.kind === 'update-state') {
					const state = data.state as ReflectoraState;
					renderStage(state);
				}
			},
		},
	);

	const renderStage = (state: ReflectoraState) => {
		const s = state.stage;

		console.log('stage:', s);
		switch (s) {
			case 'starting':
				render(view, <div class="animate-pulse text-xl text-gray-700">Starting play session</div>);
				break;
			case 'register-players':
				render(
					view,
					<PlayerRegisterView
						onContinue={() => {
							console.log('start the game');
							rpc.sendReflectoraMessage.query({ sessionId, update: { kind: 'start-game' } });
						}}
						state={state}
					/>,
				);
				break;
			case 'answer-questions':
				{
					const cols = new Map();
					cols.set('Vragen Beantwoord', (p: ReflectoraPlayerState) => `${(p.answers ?? []).filter((a) => a != null && a.length > 0).length} / 4`);

					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Vragen aan het beantwoorden</h2>
							<Button
								on:click={() => {
									rpc.sendReflectoraMessage.query({ sessionId, update: { kind: 'end-questions' } });
								}}
							>
								Door naar volgende stap
							</Button>

							<PlayerTable state={state} cols={cols} />
						</div>,
					);
				}
				break;
			case 'play-rounds':
				{
					const curPlayer = state.players[state.currentPlayerIndex];

					const cols = new Map();
					cols.set('Reflectiepunten', (p: ReflectoraPlayerState) => {
						let reflectionScore = p.themeScores ? p.themeScores.reduce((acc, cur) => acc + cur, 0) : 0;
						if (isNaN(reflectionScore)) reflectionScore = 0;
						if (p.amuletScore) reflectionScore += p.amuletScore;

						return reflectionScore;
					});
					cols.set("Thema's behandeld", (p: ReflectoraPlayerState) => p.themesPresented.join(', '));
					cols.set('Huidig thema', (p: ReflectoraPlayerState) =>
						p.roundThemeIndex === undefined ? '-' : `${p.roundThemeIndex + 1}: ${p.answers[p.roundThemeIndex]}`,
					);

					const unratedConclusions = state.players.flatMap((p) => {
						if (!p.themeScores) p.themeScores = [];
						console.log(p.themesPresented, p.themeScores);
						const themesPresentedButNotRated = p.themesPresented.filter((t) => !p.themeScores[t]);
						return themesPresentedButNotRated.map((themeIndex) => ({
							player: p,
							themeIndex,
						}));
					});

					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Rondes spelen</h2>
							<div>
								Ronde {state.roundIndex} - {state.players[state.currentPlayerIndex].name} (fase {curPlayer.phase + 1})
							</div>
							{unratedConclusions.length === 0 && (
								<ToggleField
									compact
									name="lastRound"
									label="Laatste ronde van spel"
									value={state.lastRound}
									onChange={(v) => {
										const lastRound = v || false;
										rpc.sendReflectoraMessage.query({ sessionId, update: { kind: 'set-is-last-round', lastRound } });
									}}
								/>
							)}
							<div class="size-8"></div>
							{curPlayer.lastScreen.kind === 'show-present-answer-screen' && (
								<Button
									on:click={() => {
										rpc.sendReflectoraMessage.query({ sessionId, update: { kind: 'finish-presenting' } });
									}}
								>
									Presenteren Beëindigen
								</Button>
							)}
							{curPlayer.lastScreen.kind === 'show-evaluation-results-screen' && (
								<Button
									on:click={() => {
										rpc.sendReflectoraMessage.query({ sessionId, update: { kind: 'finish-evaluation' } });
									}}
								>
									Evalueren Beëindigen
								</Button>
							)}
							{curPlayer.lastScreen.kind === 'show-conclusion-results-screen' && (
								<Button
									on:click={() => {
										rpc.sendReflectoraMessage.query({ sessionId, update: { kind: 'finish-conclusion' } });
									}}
								>
									Conclusie Beëindigen
								</Button>
							)}
							{/* {curPlayer.lastScreen.kind === 'show-conclude-wait-screen' && (
								<>
									<strong>Speler notities:</strong>
									<pre class="mb-8 w-fit rounded-md border border-gray-400 bg-gray-50 p-2 text-sm">
										{curPlayer.notes[curPlayer.roundThemeIndex!]?.trim() || 'geen notities'}
									</pre>

									<div>
										<label class="block font-bold">Conclusie Munten:</label>
										<input
											id="conclusion-rating"
											type="number"
											class="mb-2 rounded-md border border-gray-300 bg-gray-50 p-1"
										/>
									</div>
									<Button
										class="mb-8"
										on:click={() => {
											rpc.sendReflectoraMessage.query({
												sessionId,
												update: {
													kind: 'rate-conclusion',
													rating: (document.querySelector('#conclusion-rating') as HTMLInputElement)
														.valueAsNumber,
												},
											});
										}}
									>
										Conclusie Beëindigen
									</Button>
								</>
							)} */}
							{curPlayer.lastScreen.kind === 'show-conclude-rating-screen' && (
								<Button
									on:click={() => {
										rpc.sendReflectoraMessage.query({ sessionId, update: { kind: 'finish-conclusion' } });
									}}
								>
									Volgende ronde
								</Button>
							)}

							<PlayerTable state={state} sessionId={sessionId} cols={cols} />

							<div class="mt-6">
								<h2 class="text-xl font-bold">Te beoordelen conclusies</h2>
								{unratedConclusions.length === 0 && <small>Alle conclusies zijn beoordeeld.</small>}
								{unratedConclusions.length > 0 && (
									<div class="flex flex-col gap-5">
										{unratedConclusions.map((c) => {
											return (
												<div>
													<h3 class="font-bold">Thema: {c.player.answers[c.themeIndex]}</h3>
													<p class="rounded-md border border-gray-300 bg-gray-50 p-2">
														{c.player.notes[c.themeIndex]}
													</p>
													<div class="mt-2 flex gap-2">
														<button
															on:click={() =>
																rpc.sendReflectoraMessage.query({
																	sessionId,
																	update: {
																		kind: 'rate-conclusion',
																		playerId: c.player.id,
																		themeIndex: c.themeIndex,
																		rating: 100,
																	},
																})
															}
															class="rounded-md bg-danger-100 px-3 py-1 text-danger-900"
														>
															Slecht
														</button>
														<button
															on:click={() =>
																rpc.sendReflectoraMessage.query({
																	sessionId,
																	update: {
																		kind: 'rate-conclusion',
																		playerId: c.player.id,
																		themeIndex: c.themeIndex,
																		rating: 150,
																	},
																})
															}
															class="rounded-md bg-warn-100 px-3 py-1 text-warn-900"
														>
															Redelijk
														</button>
														<button
															on:click={() =>
																rpc.sendReflectoraMessage.query({
																	sessionId,
																	update: {
																		kind: 'rate-conclusion',
																		playerId: c.player.id,
																		themeIndex: c.themeIndex,
																		rating: 200,
																	},
																})
															}
															class="rounded-md bg-success-100 px-3 py-1 text-success-900"
														>
															Goed
														</button>
													</div>
												</div>
											);
										})}
									</div>
								)}
							</div>
						</div>,
					);
				}
				break;
			case 'ending-report':
				{
					const cols = new Map();
					cols.set('Vragen', (p: ReflectoraPlayerState) =>
						p.answers.map((a) => (
							<div>
								<span>{a}</span>
							</div>
						)),
					);

					render(
						view,
						<div>
							<p>Showing ending report</p>
							<PlayerTable state={state} cols={cols} />
						</div>,
					);
				}
				break;
		}
	};

	// render initial state
	(async () => {
		const state = await rpc.getGameState.query({ sessionId });
		console.log(state);
		renderStage(state);
	})();

	return view;
}

function PlayerRegisterView({ state, onContinue }: { state: ReflectoraState; onContinue: () => void }) {
	console.log('render players', state.players);

	const playersWithName = state.players.filter((p) => p.name);

	return (
		<div>
			<h3 class="text-lg font-bold text-gray-800 dark:text-gray-200">Spelers registreren</h3>
			<p class="mb-5 text-gray-700">
				Laat spelers de QR code op het bord scannen en vervolgens hun gegevens invullen.
				<br />
				Druk op <strong>doorgaan</strong> als alle spelers gereed zijn.
			</p>
			{playersWithName.length > 0 && (
				<Table>
					<thead>
						<TrHead>
							<Th>Name</Th>
							<Th>Color</Th>
							<Th>Id</Th>
						</TrHead>
					</thead>
					<tbody>
						{playersWithName.map((player) => {
							return (
								<TrBody>
									<Td>{player.name}</Td>
									<Td>{player.color}</Td>
									<Td>{player.id}</Td>
								</TrBody>
							);
						})}
					</tbody>
				</Table>
			)}
			{!playersWithName.length && <small class="text-gray-700">Er zijn nog geen spelers.</small>}
			<div class="mt-5"></div>
			{!!playersWithName.length && <Button on:click={onContinue}>Doorgaan</Button>}
		</div>
	);
}

function PlayerTable({
	state,
	sessionId,
	cols,
}: {
	state: ReflectoraState;
	sessionId?: string;
	cols?: Map<string, (p: ReflectoraPlayerState) => JSXChild>;
}) {
	return (
		<Table>
			<thead>
				<TrHead>
					{sessionId && <Th title="When disabled the players turn will always be skipped">Active</Th>}
					<Th>Name</Th>
					<Th>Color</Th>
					<Th>Munten</Th>
					{/* <Th>Antwoord</Th> */}
					{cols && [...cols.entries()].map(([k, _]) => <Th>{k}</Th>)}
					<Th>id</Th>
				</TrHead>
			</thead>
			<tbody>
				{state.players.map((player) => {
					return (
						<TrBody>
							{sessionId && (
								<Td>
									<CheckboxField
										name={player.id + '-active'}
										label={''}
										value={player.active}
										onChange={(v) => {
											rpc.sendFlikkenMessage.query({
												sessionId,
												update: { kind: 'set-player-active', playerId: player.id, active: v ?? true },
											});
										}}
									/>
								</Td>
							)}
							<Td>{player.name}</Td>
							<Td>{player.color}</Td>
							<Td>{player.score}</Td>
							{cols && [...cols.entries()].map(([_, v]) => <Td>{v(player)}</Td>)}
							{/* <Td>{player.roundAnswers[state.roundIndex]?.emotion ?? ''}</Td> */}
							<Td>{player.id}</Td>
						</TrBody>
					);
				})}
			</tbody>
		</Table>
	);
}
